import { pricingPlansModalOpenAtom } from '../../billingAtoms';
import { useIsPracticeMaxUpgradeAvailable } from '../../hooks/useIsPracticeMaxUpgradeAvailable';
import { useSubscriptionUpgrade } from '../../hooks/useSubscriptionUpgrade';
import { useUpdateExistingSubscription } from '../../hooks/useUpdateExistingSubscription';
import { isCancelPendingSubscription as getIsCancelPendingSubscription } from '../../utils/isCancelPendingSubscription';

import { type Period } from '@heidi/commerce/contracts';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import {
  useUserBillingInfo as useQueryUserBillingInfo,
  useReverseTrial,
} from '../../../../../data/injected-billing';
import { isClinicianMaxBillingSeat } from '../../../is-clinician-max-billing-seat';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';

export function usePricingPlansModalState() {
  const setIsOpen = useSetAtom(pricingPlansModalOpenAtom);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const { data: billingInfo } = useQueryUserBillingInfo();
  const legacyTier = billingInfo?.tier;
  const currentPlan = billingInfo?.new_product_tier;
  const currentSeatType = billingInfo?.seat_type;

  const { isInReverseTrial, isFakeClinicianTrial } = useReverseTrial();
  const { isInFreeTrial: isEvidenceInFreeTrial } =
    getCommerceBillingUiPorts().useEvidenceBillingInfo();

  const isTrialingClinician = isInReverseTrial || isFakeClinicianTrial;
  /**
   * Standard = `CLINICIAN_PRO` and not MAX. Absent `seat_type` still reads as Standard.
   */
  const isClinicianMaxSeat = isClinicianMaxBillingSeat(billingInfo);
  const isClinicianStandardPlan =
    currentPlan === SubscriptionTier.CLINICIAN_PRO && !isClinicianMaxSeat;
  const isPracticeMaxSeat =
    currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS &&
    currentSeatType === BillingSeatType.MAX;
  const isPracticeStandardPlan =
    currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS && !isPracticeMaxSeat;
  /** Flag on and the (CLINICIAN_PRO_PLUS, MAX) row listed: the Practice column offers a Max seat. */
  const { isPracticeMaxUpgradeAvailable: offersPracticeMax, hasPracticeMaxProduct } =
    useIsPracticeMaxUpgradeAvailable();
  /**
   * A paying Practice team gets the single Practice card into the seat checkout
   * instead of the plan columns, whatever the viewer's own seat: an admin on Max
   * may still have members to move. With the team upgrades flag off the
   * Practice column keeps its Practice Max offer.
   */
  const isTeamUpgradesEnabled =
    getCommerceBillingUiPorts().useIsHeidi2TeamUpgradesEnabled();
  const isPracticeSeatCheckoutOffered =
    isTeamUpgradesEnabled &&
    !getCommerceBillingUiPorts().isAppleCompliantBuild() &&
    currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS &&
    !isTrialingClinician &&
    hasPracticeMaxProduct;
  /**
   * Scribe Team is the grandfathered team plan. Its upgrade moves the whole
   * team onto Practice today, Standard or Max, through the seat checkout.
   */
  const isScribeTeamUpgradeOffered =
    isTeamUpgradesEnabled &&
    !getCommerceBillingUiPorts().isAppleCompliantBuild() &&
    currentPlan === SubscriptionTier.PRO_PLUS &&
    !isTrialingClinician;
  /**
   * Clinician Max uses the same Practice seat-checkout card as Scribe Team
   * (Standard + Max prices into `/settings/billing/upgrade`). Without the
   * team upgrades flag it keeps the Practice Max-only plan column.
   */
  const isClinicianMaxSeatUpgradeOffered =
    isTeamUpgradesEnabled &&
    !getCommerceBillingUiPorts().isAppleCompliantBuild() &&
    isClinicianMaxSeat &&
    !isTrialingClinician;
  const isTrialing = isInReverseTrial || isFakeClinicianTrial || isEvidenceInFreeTrial;

  const isCancelPendingSubscription = getIsCancelPendingSubscription(
    billingInfo,
    isTrialing
  );

  const { redirectToBillingPortalReactivate } = useUpdateExistingSubscription();

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });
  const { data: products, catalogueVersion } =
    getCommerceBillingUiPorts().products.useQueryProducts({
      heidi2Catalogue: true,
    });
  const handlePlanCheckout = useCallback(
    (period: Period, tier: SubscriptionTier, seatType = BillingSeatType.STANDARD) => {
      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
      // v3 rows checkout by `price_id` (tier + seat). A Max seat exists on v3
      // only, so `null` blocks it rather than falling back to tier checkout.
      const priceId =
        catalogueVersion === 'v3'
          ? (getCommerceBillingUiPorts().products.getPlanFromPeriod(
              period,
              getCommerceBillingUiPorts().products.getNewProductTierProduct(
                tier,
                products,
                seatType
              )
            )?.id ?? null)
          : seatType === BillingSeatType.MAX
            ? null
            : undefined;
      void handleUpgrade(period, tier, priceId);
    },
    [catalogueVersion, handleUpgrade, products]
  );

  const handleReactivateSubscription = useCallback(() => {
    if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
    void redirectToBillingPortalReactivate(handleClose);
  }, [handleClose, redirectToBillingPortalReactivate]);

  return {
    handlePlanCheckout,
    handleReactivateSubscription,
    handleClose,
    isPending,
    legacyTier,
    currentPlan,
    currentSeatType,
    isTrialingClinician,
    isClinicianStandardPlan,
    isClinicianMaxSeat,
    isPracticeStandardPlan,
    isPracticeMaxSeat,
    offersPracticeMax,
    isPracticeSeatCheckoutOffered,
    isScribeTeamUpgradeOffered,
    isClinicianMaxSeatUpgradeOffered,
    /**
     * Rising-tide Practice seat page (`/settings/billing/upgrade` → invoice-preview).
     * Every plan except Practice Standard picks one Practice seat for the whole
     * team there. Practice Standard alone uses per-member Max scheduling.
     */
    isPracticeRisingTideCheckoutOffered:
      isTeamUpgradesEnabled &&
      !getCommerceBillingUiPorts().isAppleCompliantBuild() &&
      !isTrialingClinician &&
      !isPracticeStandardPlan,
    isCancelPendingSubscription,
    isInReverseTrial,
    isFakeClinicianTrial,
    /** Reverse / fake clinician / evidence free trial (not complimentary usage). */
    isTrialing,
  };
}

export type UsePricingPlansModalStateReturn = ReturnType<
  typeof usePricingPlansModalState
>;
